import styled from 'foundations-theming/utils';
import { OWNER } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { CRM_OBJECT } from 'customer-data-objects/externalOptions/ExternalOptionsReferenceTypes';
import ReferenceLiteName from 'reference-resolvers-lite/components/ReferenceLiteName';
import AssetLink from 'framework-asset-preview-lib/internal/shared/components/AssetLink';
import { getPropertyValue, getStatusPropertyName } from 'framework-asset-preview-lib/internal/shared/utils/crmObject';
import StatusPropertySection from 'framework-asset-preview-lib/internal/properties/components/propertySections/StatusPropertySection';
import UserPropertySection from 'framework-asset-preview-lib/internal/properties/components/propertySections/UserPropertySection';
import { OBJECT_TYPE } from 'framework-asset-preview-lib/internal/shared/constants/objectTypes';
import AssetPreview from 'framework-asset-preview-lib/components/AssetPreview';
import { useAssetPreview } from 'framework-asset-preview-lib/internal/shared/providers/AssetPreviewProvider';
import { getPopoverPreview } from 'framework-asset-preview-lib/internal/assetPreview/utils/getPreviewComponent';
import { getJourneyObjectIdFromWorkflow } from 'framework-asset-preview-lib/internal/associations/utils/automationJourneys';
import { getLinkProperty } from 'framework-asset-preview-lib/internal/shared/utils/crmObject';
import { useMemo } from 'react';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledAssetContainer = styled.div.withConfig({
  displayName: "AssociatedAsset__StyledAssetContainer"
})(["display:flex;flex-direction:column;border:1px solid ", ";border-radius:3px;padding:12px;margin-bottom:8px;:hover{box-shadow:0 0 25px -2px ", ";outline:2px solid ", ";}"], ({
  theme
}) => theme.color['border-core-subtle'], ({
  theme
}) => theme.color['fill-info-subtle'], ({
  theme
}) => theme.color['border-interactive-default']);
const StyledAssetContainerWithPopover = styled(StyledAssetContainer).withConfig({
  displayName: "AssociatedAsset__StyledAssetContainerWithPopover"
})(["cursor:pointer;"]);
export default function AssociatedAsset({
  associatedObject,
  associationGroup,
  crmObject,
  onHoverAsset,
  openPopover,
  properties
}) {
  const {
    applicationId,
    objectType
  } = useAssetPreview();
  const hasPopoverPreview = associatedObject.objectTypeId !== OBJECT_TYPE.TRACKING_URL && getPopoverPreview(associatedObject.objectTypeId);
  const WrapperContainer = hasPopoverPreview ? StyledAssetContainerWithPopover : StyledAssetContainer;
  const propertyValueForLink = getPropertyValue(associatedObject, getLinkProperty(associatedObject.objectTypeId, associationGroup.toCrmObjectTypeDefinition.primaryDisplayLabelPropertyName));
  const statusPropertyName = getStatusPropertyName(associatedObject);
  const hsStatus = statusPropertyName ? getPropertyValue(associatedObject, statusPropertyName) : undefined;

  // Dev Note: Stages are part of an automation journey.
  // They are workflows that cannot not be exposed in the workflows app:
  // They should instead be treated as a piece of a journey object,
  // with all routing taking the user to the journey app.
  const isAutomationJourneyStage = associationGroup.toObjectTypeId === OBJECT_TYPE.AUTOMATION_JOURNEY && associatedObject.objectTypeId === OBJECT_TYPE.WORKFLOW;
  const maybeJourneyObjectId = getJourneyObjectIdFromWorkflow(associatedObject);
  const linkedObject = isAutomationJourneyStage && maybeJourneyObjectId ? {
    objectTypeId: OBJECT_TYPE.AUTOMATION_JOURNEY,
    objectId: maybeJourneyObjectId,
    portalId: associatedObject.portalId,
    properties: {}
  } : associatedObject;
  const fromCampaignQueryParam = useMemo(() => {
    if (crmObject.objectTypeId === OBJECT_TYPE.CAMPAIGN) {
      return `fromCampaignId=${getPropertyValue(crmObject, 'hs_origin_asset_id')}`;
    }
    return '';
  }, [crmObject]);
  const associatedObjectTile = /*#__PURE__*/_jsxs(WrapperContainer, {
    onMouseEnter: () => onHoverAsset(associatedObject.objectId),
    "data-test-id": "associated-asset",
    children: [isAutomationJourneyStage && /*#__PURE__*/_jsx("b", {
      children: /*#__PURE__*/_jsx(ReferenceLiteName, {
        id: getJourneyObjectIdFromWorkflow(associatedObject),
        objectTypeId: OBJECT_TYPE.AUTOMATION_JOURNEY,
        referenceType: CRM_OBJECT
      })
    }), associatedObject.objectTypeId === OBJECT_TYPE.TRACKING_URL ? /*#__PURE__*/_jsx(_Fragment, {
      children: propertyValueForLink || associatedObject.objectId
    }) : /*#__PURE__*/_jsx(AssetLink, {
      crmObject: linkedObject,
      linkQueryParams: fromCampaignQueryParam,
      children: propertyValueForLink || associatedObject.objectId
    }), hsStatus && /*#__PURE__*/_jsx(View, {
      marginBlockStart: "200",
      children: /*#__PURE__*/_jsx(StatusPropertySection, {
        crmObject: associatedObject,
        hideLabel: true,
        useComponent: "UIStatusTag"
      })
    }), associatedObject.objectTypeId === OBJECT_TYPE.CAMPAIGN && /*#__PURE__*/_jsx(View, {
      marginBlockStart: "200",
      children: /*#__PURE__*/_jsx(UserPropertySection, {
        crmObject: associatedObject,
        properties: properties,
        propertyName: "hs_owner",
        resolvedObjectType: OWNER
      })
    })]
  }, associatedObject.objectId);
  if (hasPopoverPreview) {
    return /*#__PURE__*/_jsx(AssetPreview, {
      applicationId: applicationId,
      objectId: associatedObject.objectId,
      objectType: associatedObject.objectTypeId,
      originObjectType: objectType,
      popoverProps: {
        closeOnOutsideClick: true,
        open: openPopover,
        onOpenChange: event => {
          // Close the popover when interacting with other areas of the page.
          if (openPopover && !event.target.value) {
            onHoverAsset(0);
          }
        },
        placement: 'left',
        use: 'longform'
      },
      previewType: "popover",
      children: associatedObjectTile
    }, associatedObject.objectId);
  }
  return associatedObjectTile;
}